import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ErrorIcon from "@mui/icons-material/Error";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import DashboardIcon from "@mui/icons-material/Dashboard";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ReceiptIcon from "@mui/icons-material/Receipt";
import TaskIcon from "@mui/icons-material/Task";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Images
import house1 from "../../pictures/project1.jpg";
import house2 from "../../pictures/project2.jpg";
import house3 from "../../pictures/project3.jpg";
import house4 from "../../pictures/project4.jpg";

import emmetrics1 from "../../pictures/emmetrics_dash.jpg";
import emmetrics2 from "../../pictures/emmetrics_exp.jpg";
import emmetrics3 from "../../pictures/emmetrics_payment.jpg";
import emmetrics4 from "../../pictures/emmetrics_po.jpg";
import emmetrics5 from "../../pictures/emmetrics_task.jpg";
import emmetrics6 from "../../pictures/emmetrics_material.jpg";

const BLUE_THEME = {
  primary: "#0d0651",
  primaryLight: "#1e1b72",
  primaryDark: "#050027",
  accent: "#2196F3",
  accentLight: "#42a5f5",
  accentDark: "#1976d2",
};

const emmetricsFeatures = [
  {
    icon: <FilterListIcon />,
    title: "Custom Fields",
    subtitle:
      "Tailor tasks to match your workflow. Add dropdowns, dates, tags and more to track exactly what matters.",
  },
  {
    icon: <DownloadIcon />,
    title: "Quick Export Tool",
    subtitle:
      "Export your projects, tasks, timesheets, and documents to CSV and Excel formats.",
  },
  {
    icon: <AssessmentIcon />,
    title: "Advanced Reports",
    subtitle:
      "Create reports and views based on selected criteria. Define your fields, filters, and sorting order then save your report for future use.",
  },
  {
    icon: <ListAltIcon />,
    title: "Task Management",
    subtitle:
      "Create, assign, organize and execute tasks with ease. Time and progress tracked through the project lifecycle.",
  },
  {
    icon: <TaskIcon />,
    title: "Time Approval",
    subtitle:
      "Fill timesheets and submit them for approval. Once approved, entries cannot be edited, for better control.",
  },
  {
    icon: <ReceiptIcon />,
    title: "Expense Tracking",
    subtitle:
      "Expense tracking projects and tasks. Attach supporting documents related to each expense.",
  },
  {
    icon: <MonetizationOnIcon />,
    title: "Expense Approval",
    subtitle:
      "Expenses approved or rejected by your project manager. Keep expenses under control for better budget management.",
  },
  {
    icon: <DashboardIcon />,
    title: "Expense Reports",
    subtitle:
      "Generate advanced expense reports based on numerous criteria and filters. Keep an eye on your project costs.",
  },
];

const carouselStyles = `
  .swiper-pagination-bullet {
    background-color: rgba(13, 6, 81, 0.45) !important;
    width: 10px !important;
    height: 10px !important;
    margin: 0 5px !important;
    opacity: 1 !important;
  }

  .swiper-pagination-bullet-active {
    background-color: #2196F3 !important;
    transform: scale(1.15);
  }

  .swiper-pagination {
    bottom: 16px !important;
    z-index: 9 !important;
  }

  .plan-carousel .swiper-wrapper,
  .emmetrics-carousel .swiper-wrapper {
    height: 100%;
  }
`;

const getPlanImages = (title) => {
  const imageMap = {
    Essential: [house1, house2, house3],
    "Premium+": [house2, house3, house4, house1],
    Premium: [house2, house3, house4, house1],
    Custom: [house3, house4, house1, house2],
    Basic: [house1, house2, house3, house4],
  };

  return imageMap[title] || [house1, house2, house3, house4];
};

const getEmmetricsImages = (title) => {
  const imageMap = {
    Essential: [emmetrics1, emmetrics2, emmetrics3],
    "Premium+": [emmetrics2, emmetrics3, emmetrics4, emmetrics1, emmetrics5, emmetrics6],
    Premium: [emmetrics2, emmetrics3, emmetrics4, emmetrics1, emmetrics5],
    Custom: [emmetrics3, emmetrics4, emmetrics1, emmetrics2, emmetrics5, emmetrics6],
    Basic: [emmetrics1, emmetrics2, emmetrics3, emmetrics4],
  };

  return imageMap[title] || [emmetrics1, emmetrics2, emmetrics3, emmetrics4];
};

const sectionCardSx = {
  p: { xs: 2.5, sm: 3, md: 4 },
  borderRadius: 4,
  border: `1px solid ${BLUE_THEME.accent}22`,
  background: "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(248,250,255,0.92))",
  boxShadow: `0 14px 38px ${BLUE_THEME.primaryDark}14`,
  backdropFilter: "blur(12px)",
};

const infoCardSx = {
  p: { xs: 2.25, sm: 3 },
  borderRadius: 4,
  border: `1px solid ${BLUE_THEME.accent}20`,
  background: `linear-gradient(145deg, ${BLUE_THEME.accent}08, ${BLUE_THEME.primary}06)`,
  boxShadow: `0 10px 30px ${BLUE_THEME.primaryDark}12`,
};

const navButtonSx = (side) => ({
  position: "absolute",
  [side]: 12,
  top: "50%",
  transform: "translateY(-50%)",
  background: "rgba(13, 6, 81, 0.72)",
  color: "white",
  zIndex: 10,
  backdropFilter: "blur(10px)",
  width: 44,
  height: 44,
  "&:hover": {
    background: BLUE_THEME.primary,
    transform: "translateY(-50%) scale(1.08)",
  },
});

const TiltCard = ({ children, sx = {} }) => {
  const [transform, setTransform] = useState(
    "perspective(1200px) rotateX(0deg) rotateY(0deg) translateY(0px)"
  );

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const rotateY = ((x / rect.width) - 0.5) * 12;
    const rotateX = ((y / rect.height) - 0.5) * -12;

    setTransform(
      `perspective(1200px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-8px) scale(1.02)`
    );
  };

  const handleLeave = () => {
    setTransform("perspective(1200px) rotateX(0deg) rotateY(0deg) translateY(0px) scale(1)");
  };

  return (
    <Card
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: "24px",
        background: "rgba(255,255,255,0.82)",
        border: "1px solid rgba(33,150,243,0.14)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        boxShadow: "0 14px 35px rgba(13,6,81,0.10)",
        transition:
          "transform 0.25s ease-out, box-shadow 0.35s ease, border-color 0.35s ease",
        transform,
        transformStyle: "preserve-3d",
        willChange: "transform",
        height: "100%",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.02))",
          pointerEvents: "none",
        },
        "&:hover": {
          boxShadow: "0 24px 60px rgba(13,6,81,0.18)",
          borderColor: "rgba(33,150,243,0.28)",
        },
        ...sx,
      }}
    >
      {children}
    </Card>
  );
};

const SectionTitle = ({ title }) => (
  <Typography
    variant="h5"
    fontWeight={800}
    sx={{
      color: BLUE_THEME.primary,
      mb: 3,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      fontSize: { xs: "1.2rem", md: "1.45rem" },
    }}
  >
    <Box
      sx={{
        width: 4,
        height: 28,
        borderRadius: 999,
        background: `linear-gradient(180deg, ${BLUE_THEME.accent}, ${BLUE_THEME.primary})`,
      }}
    />
    {title}
  </Typography>
);

const ImageCarousel = ({
  images = [],
  title = "",
  className = "",
  setSwiperRef,
  swiperRef,
}) => (
  <Card
    sx={{
      borderRadius: 4,
      overflow: "hidden",
      border: `1px solid ${BLUE_THEME.accent}20`,
      boxShadow: `0 18px 45px ${BLUE_THEME.primaryDark}16`,
      position: "relative",
      background: "#fff",
    }}
  >
    <Box sx={{ height: { xs: 280, sm: 340, md: 400 } }}>
      <Swiper
        onSwiper={setSwiperRef}
        modules={[Navigation, Pagination, Autoplay]}
        pagination={{ clickable: true, dynamicBullets: true }}
        autoplay={{ delay: 4000, disableOnInteraction: false }}
        loop
        className={className}
        style={{ width: "100%", height: "100%" }}
      >
        {images.map((image, idx) => (
          <SwiperSlide key={idx}>
            <Box
              component="img"
              src={image}
              alt={`${title} - Image ${idx + 1}`}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>

    <IconButton onClick={() => swiperRef?.slidePrev()} sx={navButtonSx("left")}>
      <ChevronLeftIcon sx={{ fontSize: 28 }} />
    </IconButton>

    <IconButton onClick={() => swiperRef?.slideNext()} sx={navButtonSx("right")}>
      <ChevronRightIcon sx={{ fontSize: 28 }} />
    </IconButton>
  </Card>
);

function PlanDetailsPage() {
  const navigate = useNavigate();
  const [planSwiperRef, setPlanSwiperRef] = useState(null);
  const [emmetricsSwiperRef, setEmmetricsSwiperRef] = useState(null);

  const planData = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("selectedPlan") || "null");
    } catch {
      return null;
    }
  })();

  const isUserAuthenticated = !!localStorage.getItem("user");

  const planImages = planData ? getPlanImages(planData.title) : [];
  const emmetricsImages = planData ? getEmmetricsImages(planData.title) : [];
  const isTransparentPlan = planData?.category === "100% Transparent Pricing";

  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.innerHTML = carouselStyles;
    document.head.appendChild(styleEl);
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  useEffect(() => {
    if (!isUserAuthenticated) {
      navigate("/construction-plans");
    }
  }, [isUserAuthenticated, navigate]);

  if (!isUserAuthenticated) return null;

  if (!planData) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${BLUE_THEME.primary}, ${BLUE_THEME.primaryLight})`,
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              textAlign: "center",
              p: 4,
              background: "white",
              borderRadius: 4,
              boxShadow: "0 20px 50px rgba(0,0,0,0.16)",
            }}
          >
            <ErrorIcon sx={{ fontSize: 60, color: BLUE_THEME.accent, mb: 2 }} />
            <Typography variant="h5" sx={{ color: BLUE_THEME.primary, mb: 2, fontWeight: 800 }}>
              Plan Not Found
            </Typography>
            <Typography variant="body1" sx={{ color: "rgba(0,0,0,0.65)", mb: 3 }}>
              The plan details could not be loaded. Please go back and try again.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/construction-plans")}
              sx={{
                background: `linear-gradient(45deg, ${BLUE_THEME.primary}, ${BLUE_THEME.primaryLight})`,
                color: "white",
                px: 3,
                py: 1.2,
                borderRadius: 2.5,
              }}
            >
              Back to Plans
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(180deg, ${BLUE_THEME.primary}08 0%, #f8fbff 45%, #ffffff 100%)`,
        pt: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 5, sm: 6, md: 8 },
      }}
    >
      <Container maxWidth="xl">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/construction-plans")}
          sx={{
            mb: { xs: 2.5, md: 3.5 },
            color: BLUE_THEME.primary,
            fontWeight: 700,
            fontSize: { xs: "0.95rem", md: "1rem" },
            "&:hover": {
              background: `${BLUE_THEME.accent}12`,
            },
          }}
        >
          Back to Construction Plans
        </Button>

        <Box sx={{ mb: { xs: 4, md: 5 } }}>
          <Typography
            variant="h3"
            fontWeight={900}
            sx={{
              background: `linear-gradient(45deg, ${BLUE_THEME.primary}, ${BLUE_THEME.accent})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1.25,
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              lineHeight: 1.12,
            }}
          >
            {planData.title}
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              color: `${BLUE_THEME.primaryDark}B3`,
              fontSize: { xs: "1rem", md: "1.08rem" },
              fontWeight: 500,
            }}
          >
            {planData.category}
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 3, md: 4 }}>
          <Grid item xs={12} lg={5}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <ImageCarousel
                images={planImages}
                title={planData.title}
                className="plan-carousel"
                swiperRef={planSwiperRef}
                setSwiperRef={setPlanSwiperRef}
              />

              <Card sx={infoCardSx}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: `${BLUE_THEME.primaryDark}80`,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 800,
                    mb: 2,
                  }}
                >
                  Plan Overview
                </Typography>

                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: `${BLUE_THEME.primaryDark}75`,
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Pricing
                  </Typography>
                  <Typography variant="h5" fontWeight={900} sx={{ color: BLUE_THEME.primary }}>
                    {planData.price}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: `${BLUE_THEME.primaryDark}75`,
                      display: "block",
                      mb: 0.75,
                    }}
                  >
                    Project Size
                  </Typography>

                  <Chip
                    label={planData.size}
                    sx={{
                      background: `linear-gradient(45deg, ${BLUE_THEME.accent}, ${BLUE_THEME.accentLight})`,
                      color: "white",
                      fontWeight: 700,
                    }}
                  />
                </Box>

                {planData.highlight && (
                  <Box
                    sx={{
                      p: 1.75,
                      background: `${BLUE_THEME.accent}12`,
                      borderRadius: 2.5,
                      border: `1px solid ${BLUE_THEME.accent}26`,
                      textAlign: "center",
                      mb: 2.5,
                    }}
                  >
                    <Typography
                      sx={{
                        color: BLUE_THEME.accentDark,
                        fontWeight: 800,
                        fontSize: "0.92rem",
                      }}
                    >
                      ⭐ MOST POPULAR PLAN
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={() => navigate("/contact-us")}
                  sx={{
                    background: `linear-gradient(45deg, ${BLUE_THEME.primary}, ${BLUE_THEME.primaryLight})`,
                    color: "white",
                    fontWeight: 800,
                    py: 1.5,
                    borderRadius: 2.5,
                    boxShadow: `0 8px 24px ${BLUE_THEME.primary}40`,
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: `0 12px 32px ${BLUE_THEME.primary}50`,
                    },
                  }}
                >
                  Get Quote Now
                </Button>
              </Card>

              {isTransparentPlan && (
                <>
                  <ImageCarousel
                    images={emmetricsImages}
                    title={`${planData.title} Emmetrics`}
                    className="emmetrics-carousel"
                    swiperRef={emmetricsSwiperRef}
                    setSwiperRef={setEmmetricsSwiperRef}
                  />

                  <Card sx={infoCardSx}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: `${BLUE_THEME.primaryDark}80`,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        fontWeight: 800,
                        mb: 2,
                      }}
                    >
                      Emmetrics Overview
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: `${BLUE_THEME.primaryDark}75`,
                          display: "block",
                          mb: 1,
                        }}
                      >
                        Features
                      </Typography>

                      <Typography variant="body1" fontWeight={700} sx={{ mb: 1 }}>
                        • End-to-End Construction Management Dashboard
                      </Typography>
                      <Typography variant="body1" fontWeight={700} sx={{ mb: 1 }}>
                        • Real-time Cost Tracking and Transparency
                      </Typography>
                      <Typography variant="body1" fontWeight={700}>
                        • Weekly Progress Updates with Photo Documentation
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={() => navigate("/contact-us")}
                      sx={{
                        background: `linear-gradient(45deg, ${BLUE_THEME.primary}, ${BLUE_THEME.primaryLight})`,
                        color: "white",
                        fontWeight: 800,
                        py: 1.5,
                        borderRadius: 2.5,
                        boxShadow: `0 8px 24px ${BLUE_THEME.primary}40`,
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: `0 12px 32px ${BLUE_THEME.primary}50`,
                        },
                      }}
                    >
                      Click here to visit Emmetrics Dashboard
                    </Button>
                  </Card>
                </>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Card sx={sectionCardSx}>
                <SectionTitle title="Complete Specifications" />

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: `${BLUE_THEME.primaryDark}75`,
                    mb: 2.5,
                    fontSize: "0.92rem",
                  }}
                >
                  {Array.isArray(planData.specs) ? planData.specs.length : 0} key features and specifications included
                </Typography>

                <List sx={{ p: 0 }}>
                  {(planData.specs || []).map((spec, idx) => (
                    <ListItem
                      key={idx}
                      sx={{
                        py: { xs: 1.5, md: 1.8 },
                        px: { xs: 1.5, md: 2 },
                        mb: 1.2,
                        borderRadius: 2.5,
                        background: `linear-gradient(135deg, ${BLUE_THEME.accent}05, transparent)`,
                        border: `1px solid ${BLUE_THEME.accent}12`,
                        transition: "all 0.25s ease",
                        "&:hover": {
                          background: `${BLUE_THEME.accent}09`,
                          transform: "translateX(6px)",
                          boxShadow: `0 6px 18px ${BLUE_THEME.primaryDark}10`,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircle
                          sx={{
                            color: BLUE_THEME.accent,
                            fontSize: { xs: 22, md: 25 },
                            filter: `drop-shadow(0 2px 6px ${BLUE_THEME.accent}30)`,
                          }}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary={spec}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: BLUE_THEME.primaryDark,
                          fontSize: { xs: "0.92rem", md: "1rem" },
                          lineHeight: 1.5,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Card>

              <Card sx={sectionCardSx}>
                <SectionTitle title="Featured Highlights" />

                <Grid container spacing={2}>
                  {(planData.features || []).slice(0, 6).map((feature, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          background: `linear-gradient(135deg, ${BLUE_THEME.accent}08, transparent)`,
                          border: `1px solid ${BLUE_THEME.accent}14`,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          height: "100%",
                          transition: "all 0.25s ease",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: `0 8px 20px ${BLUE_THEME.primaryDark}14`,
                          },
                        }}
                      >
                        <CheckCircle
                          sx={{
                            color: BLUE_THEME.accent,
                            fontSize: 24,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            color: BLUE_THEME.primaryDark,
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            lineHeight: 1.35,
                          }}
                        >
                          {feature}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Card>

              {isTransparentPlan && (
                <Card
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: 5,
                    background: "linear-gradient(145deg, #ffffff 0%, #f8fbff 100%)",
                    border: "1px solid rgba(33,150,243,0.12)",
                    boxShadow: "0 25px 70px rgba(13,6,81,0.08)",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontSize: "0.78rem",
                        fontWeight: 800,
                        letterSpacing: "0.2em",
                        color: BLUE_THEME.accent,
                        mb: 1.2,
                        textTransform: "uppercase",
                      }}
                    >
                      EMMETRICS PLATFORM
                    </Typography>

                    <Typography
                      variant="h4"
                      fontWeight={900}
                      sx={{
                        color: BLUE_THEME.primary,
                        fontSize: { xs: "1.7rem", sm: "2rem", md: "2.35rem" },
                        lineHeight: 1.12,
                      }}
                    >
                      Our Amazing Features
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    {emmetricsFeatures.map((feature, index) => (
                      <Grid item xs={12} sm={6} xl={4} key={index}>
                        <TiltCard>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 2,
                              height: "100%",
                              position: "relative",
                              zIndex: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 64,
                                height: 64,
                                borderRadius: "18px",
                                background: `linear-gradient(135deg, ${BLUE_THEME.primary}, ${BLUE_THEME.accent})`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                boxShadow: "0 10px 24px rgba(33,150,243,0.25)",
                                transform: "translateZ(30px)",
                              }}
                            >
                              {React.cloneElement(feature.icon, {
                                sx: { fontSize: 30, color: "white" },
                              })}
                            </Box>

                            <Box sx={{ flex: 1, transform: "translateZ(22px)" }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  color: BLUE_THEME.primary,
                                  fontWeight: 800,
                                  fontSize: "1.02rem",
                                  lineHeight: 1.3,
                                  mb: 1,
                                }}
                              >
                                {feature.title}
                              </Typography>

                              <Typography
                                sx={{
                                  color: "rgba(5,0,39,0.78)",
                                  fontSize: "0.94rem",
                                  lineHeight: 1.62,
                                }}
                              >
                                {feature.subtitle}
                              </Typography>
                            </Box>
                          </Box>
                        </TiltCard>
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              )}
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: { xs: 5, md: 8 },
            p: { xs: 3, sm: 4, md: 5 },
            background: `linear-gradient(135deg, ${BLUE_THEME.primary}, ${BLUE_THEME.primaryLight})`,
            borderRadius: 4,
            textAlign: "center",
            color: "white",
            boxShadow: "0 20px 50px rgba(13,6,81,0.18)",
          }}
        >
          <Typography
            variant="h5"
            fontWeight={900}
            sx={{ mb: 1.5, fontSize: { xs: "1.3rem", md: "1.55rem" } }}
          >
            Ready to Get Started?
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 3,
              opacity: 0.95,
              fontSize: { xs: "0.95rem", md: "1rem" },
            }}
          >
            Connect with our team to transform your vision into reality
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/contact-us")}
              sx={{
                background: "white",
                color: BLUE_THEME.primary,
                fontWeight: 800,
                px: 4,
                py: 1.5,
                borderRadius: 2.5,
                "&:hover": {
                  background: "#ffffff",
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 35px rgba(0,0,0,0.25)",
                },
              }}
            >
              Get Quote
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate("/contact-us")}
              sx={{
                borderColor: "white",
                color: "white",
                fontWeight: 800,
                px: 4,
                py: 1.5,
                borderRadius: 2.5,
                "&:hover": {
                  background: "rgba(255,255,255,0.10)",
                  borderColor: "white",
                },
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default PlanDetailsPage;